"use client";

import { Device, Area, EnergyData, UserPreferences, Activity, DeviceState } from "@/types";
import { initialDevices, initialAreas, initialEnergyData, initialUserPreferences, initialActivities, initialAlerts, initialScenes, initialSchedules, initialUsers, initialBacnetObjects } from "./mock-database";

type DeviceStateChangeCallback = (deviceId: string, state: DeviceState) => void;

interface Alert {
  id: string;
  name: string;
  alertType: "device" | "system" | "energy";
  method: "email";
  recipients?: string[];
  enabled: boolean;
  threshold?: number;
  area?: string;
}

export class MockDatabase {
  private devices: Device[];
  private areas: Area[];
  private energyData: EnergyData[];
  private userPreferences: UserPreferences;
  private activities: Activity[];
  private alerts: Alert[];
  private static instance: MockDatabase;
  private deviceStateSubscribers: Map<string, DeviceStateChangeCallback>;

  protected constructor() {
    // Initialize with default values first
    this.devices = [...initialDevices];
    this.areas = [...initialAreas];
    this.energyData = [...initialEnergyData];
    this.userPreferences = { ...initialUserPreferences };
    this.activities = [...initialActivities];
    this.alerts = [...initialAlerts];
    this.deviceStateSubscribers = new Map();

    // Only try to load from localStorage if we're in the browser
    if (typeof window !== 'undefined') {
      try {
    const storedData = localStorage.getItem("mockDatabase");
    if (storedData) {
      const data = JSON.parse(storedData);
      this.devices = data.devices || initialDevices;
      this.areas = data.areas || initialAreas;
      this.energyData = data.energyData || initialEnergyData;
      this.userPreferences = data.userPreferences || initialUserPreferences;
      this.activities = data.activities || initialActivities;
      this.alerts = data.alerts?.length ? data.alerts : [...initialAlerts];
    } else {
      this.saveToLocalStorage();
        }
      } catch (error) {
        console.warn('Failed to load mock database from localStorage:', error);
      }
    }
  }

  public static getInstance(): MockDatabase {
    if (!MockDatabase.instance) {
      MockDatabase.instance = new MockDatabase();
    }
    return MockDatabase.instance;
  }

  private saveToLocalStorage(): void {
    if (typeof window !== 'undefined') {
      try {
    const dataToSave = {
      devices: this.devices,
      areas: this.areas,
      energyData: this.energyData,
      userPreferences: this.userPreferences,
      activities: this.activities,
      alerts: this.alerts,
    };
    localStorage.setItem("mockDatabase", JSON.stringify(dataToSave));
      } catch (error) {
        console.warn('Failed to save mock database to localStorage:', error);
      }
    }
  }

  public getDevices(): Device[] {
    return [...this.devices];
  }

  public getAreas(): Area[] {
    return [...this.areas];
  }

  public getEnergyData(): EnergyData[] {
    return [...this.energyData];
  }

  public getUserPreferences(): UserPreferences {
    return { ...this.userPreferences };
  }

  public getActivities(): Activity[] {
    return [...this.activities];
  }

  public getAlerts(): Alert[] {
    return [...this.alerts];
  }

  public getAlert(id: string): Alert | undefined {
    return this.alerts.find(alert => alert.id === id);
  }

  public updateDevice(deviceId: string, updates: Partial<DeviceState>): void {
    const deviceIndex = this.devices.findIndex((d) => d.id === deviceId);
    if (deviceIndex === -1) {
      throw new Error(`Device with id ${deviceId} not found`);
    }

    const device = this.devices[deviceIndex];
    const updatedDevice = {
      ...device,
      state: {
        ...device.state,
        ...updates,
      },
    };

    this.devices[deviceIndex] = updatedDevice;
    this.saveToLocalStorage();

    // Notify subscribers
    this.deviceStateSubscribers.forEach((callback) => {
      callback(deviceId, updatedDevice.state);
    });
  }

  public updateArea(areaId: string, updates: Partial<Area>): void {
    const areaIndex = this.areas.findIndex((a) => a.id === areaId);
    if (areaIndex === -1) {
      throw new Error(`Area with id ${areaId} not found`);
    }

    this.areas[areaIndex] = {
      ...this.areas[areaIndex],
      ...updates,
    };
    this.saveToLocalStorage();
  }

  public addEnergyData(data: Omit<EnergyData, "id">): void {
    const newData = {
      timestamp: data.timestamp,
      deviceId: data.deviceId,
      powerConsumption: data.powerConsumption || 0,
      duration: data.duration,
      voltage: data.voltage,
      current: data.current
    };
    this.energyData.push(newData);
    this.saveToLocalStorage();
  }

  public updateUserPreferences(updates: Partial<UserPreferences>): void {
    this.userPreferences = {
      ...this.userPreferences,
      ...updates,
    };
    this.saveToLocalStorage();
  }

  public addActivity(activity: Omit<Activity, "id">): void {
    const newActivity: Activity = {
      id: `activity-${Date.now()}`,
      ...activity,
    };
    this.activities.push(newActivity);
    this.saveToLocalStorage();
  }

  public createDevice(device: Omit<Device, "id">): Device {
    const newDevice: Device = {
      id: `device-${Date.now()}`,
      ...device,
      lastUpdated: new Date().toISOString()
    };
    this.devices.push(newDevice);
    this.saveToLocalStorage();
    return newDevice;
  }

  public deleteDevice(deviceId: string): Device {
    const deviceIndex = this.devices.findIndex((d) => d.id === deviceId);
    if (deviceIndex === -1) {
      throw new Error(`Device with id ${deviceId} not found`);
    }
    const deletedDevice = this.devices[deviceIndex];
    this.devices.splice(deviceIndex, 1);
    this.saveToLocalStorage();
    return deletedDevice;
  }

  public createArea(area: Omit<Area, "id">): Area {
    const newArea: Area = {
      id: `area-${Date.now()}`,
      ...area,
      devices: area.devices || []
    };
    this.areas.push(newArea);
    this.saveToLocalStorage();
    return newArea;
  }

  public deleteArea(areaId: string): Area {
    const areaIndex = this.areas.findIndex((a) => a.id === areaId);
    if (areaIndex === -1) {
      throw new Error(`Area with id ${areaId} not found`);
    }
    const deletedArea = this.areas[areaIndex];
    this.areas.splice(areaIndex, 1);
    this.saveToLocalStorage();
    return deletedArea;
  }

  public subscribe(id: string, callback: DeviceStateChangeCallback): void {
    this.deviceStateSubscribers.set(id, callback);
  }

  public unsubscribe(id: string): void {
    this.deviceStateSubscribers.delete(id);
  }

  public createAlert(alert: Omit<Alert, 'id'>): Alert {
    const newAlert = {
      ...alert,
      id: Date.now().toString(),
    };
    this.alerts.push(newAlert);
    console.log('Created alert, total alerts now:', this.alerts);
    this.saveToLocalStorage();
    return newAlert;
  }

  public updateAlert(id: string, updates: Partial<Alert>): Alert {
    const index = this.alerts.findIndex(alert => alert.id === id);
    if (index === -1) {
      throw new Error(`Alert with id ${id} not found`);
    }
    this.alerts[index] = { ...this.alerts[index], ...updates };
    this.saveToLocalStorage();
    return this.alerts[index];
  }

  public deleteAlert(id: string): void {
    const index = this.alerts.findIndex(alert => alert.id === id);
    if (index === -1) {
      throw new Error(`Alert with id ${id} not found`);
    }
    this.alerts.splice(index, 1);
    this.saveToLocalStorage();
  }
} 